import React from 'react';
import { FaSearch, FaDraftingCompass, FaCode, FaRocket } from 'react-icons/fa';

const ProcessSection = () => {
    const processSteps = [
        {
            step: "01",
            title: "Discover & Strategize",
            description: "We start by diving deep into your goals, audience, and challenges to create a data-driven strategy for success.",
            icon: <FaSearch />,
            aos: "fade-right"
        },
        {
            step: "02",
            title: "Design & Develop",
            description: "Our team designs a stunning, user-friendly interface and then brings it to life with clean, efficient code.",
            icon: <FaDraftingCompass />,
            aos: "fade-left"
        },
        {
            step: "03",
            title: "Test & Launch",
            description: "We rigorously test every aspect of the project to ensure a flawless experience before launching it to the world.",
            icon: <FaCode />,
            aos: "fade-right"
        },
        {
            step: "04",
            title: "Support & Grow",
            description: "Our partnership doesn't end at launch. We provide ongoing support and strategies to help you grow.",
            icon: <FaRocket />,
            aos: "fade-left"
        }
    ];

    return (
        <>
            <section className="process-section">
                {/* Decorative glow elements */}
                <div className="process-glow-left"></div>
                <div className="process-glow-right"></div>
                
                <div className="container relative-z">
                    <div className="section-title-wrapper text-center mb-5" data-aos="fade-up">
                        <div className="section-badge">Our Methodology</div>
                        <h2 className="section-heading">Our Proven <span className="text-gradient">Process</span></h2>
                        <p className="section-subheading mx-auto">A systematic approach to transforming your vision into a powerful digital reality.</p>
                    </div>

                    <div className="timeline-container">
                        {processSteps.map((item, index) => (
                            <div className="timeline-item" data-aos={item.aos} key={index}>
                                <div className="timeline-content glass-panel">
                                    <div className="timeline-step-bg">{item.step}</div>
                                    <div className="timeline-icon-box">{item.icon}</div>
                                    <h3 className="timeline-title">{item.title}</h3>
                                    <p className="timeline-description">{item.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <style>{`
                /* =============================================
                   PREMIUM PROCESS TIMELINE SECTION
                   ============================================= */

                .process-section {
                  background-color: #0c001f;
                  padding: 100px 0;
                  position: relative;
                  color: #fff;
                  overflow: visible; /* Prevent clipping of icons/glows */
                  z-index: 1;
                }

                .relative-z {
                    position: relative;
                    z-index: 2;
                }

                /* --- Ambient Background Glows --- */
                .process-glow-left, .process-glow-right {
                    position: absolute;
                    width: 500px;
                    height: 500px;
                    background: radial-gradient(circle, rgba(160, 79, 244, 0.15) 0%, transparent 70%);
                    border-radius: 50%;
                    z-index: 0;
                    pointer-events: none;
                }
                .process-glow-left {
                    top: -10%;
                    left: -15%;
                }
                .process-glow-right {
                    bottom: -10%;
                    right: -15%;
                    background: radial-gradient(circle, rgba(255, 180, 40, 0.1) 0%, transparent 70%);
                }

                /* --- Section Header Styling --- */
                .section-badge {
                    display: inline-block;
                    font-size: 0.85rem;
                    font-weight: 600;
                    letter-spacing: 2px;
                    text-transform: uppercase;
                    color: #a04ff4;
                    background: rgba(160, 79, 244, 0.1);
                    padding: 6px 16px;
                    border-radius: 30px;
                    border: 1px solid rgba(160, 79, 244, 0.2);
                    margin-bottom: 15px;
                }

                .process-section .section-heading {
                  font-size: 3rem;
                  font-weight: 800;
                  color: #fff;
                  margin-bottom: 1rem;
                  letter-spacing: -1px;
                }

                .text-gradient {
                  background: linear-gradient(90deg, #a04ff4, #ffb428);
                  -webkit-background-clip: text;
                  -webkit-text-fill-color: transparent;
                }

                .section-subheading {
                    font-size: 1.1rem;
                    color: #a5b0bb;
                    max-width: 600px;
                    line-height: 1.6;
                }

                /* --- Timeline Architecture --- */
                .timeline-container {
                  position: relative;
                  width: 100%;
                  max-width: 1000px; /* Reduced max-width to bring cards closer to center */
                  margin: 0 auto;
                  padding-top: 30px;
                }

                /* Glowing Central Line */
                .process-section .timeline-container::before {
                  display: none !important;
                }
                .timeline-container::after {
                  content: '';
                  position: absolute;
                  width: 3px; 
                  background: linear-gradient(to bottom, transparent, rgba(160, 79, 244, 0.8), rgba(255, 180, 40, 0.8), transparent);
                  top: 0;
                  bottom: 0;
                  left: 50%;
                  margin-left: -1.5px;
                  z-index: 1;
                }

                .timeline-item {
                  /* Reduced side padding to bring cards closer to the middle line */
                  padding: 10px 40px; 
                  position: relative;
                  width: 50%;
                  box-sizing: border-box;
                }

                .timeline-item:nth-child(odd) { left: 0; }
                .timeline-item:nth-child(even) { left: 50%; }

                /* --- Premium Glass Panel --- */
                .timeline-content.glass-panel {
                  padding: 35px 30px;
                  background: rgba(255, 255, 255, 0.03);
                  backdrop-filter: blur(16px);
                  -webkit-backdrop-filter: blur(16px);
                  border: 1px solid rgba(255, 255, 255, 0.08);
                  border-radius: 20px;
                  position: relative;
                  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
                  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                  /* removed overflow: hidden so the icon box can stick out nicely */
                }

                /* Interactive Hover State */
                .timeline-content.glass-panel:hover {
                    transform: translateY(-5px);
                    background: rgba(255, 255, 255, 0.05);
                    border-color: rgba(160, 79, 244, 0.5);
                    box-shadow: 0 15px 35px rgba(0,0,0,0.4), 0 0 15px rgba(160, 79, 244, 0.15);
                }

                /* Large Watermark Number */
                .timeline-step-bg {
                  position: absolute;
                  font-size: 7rem;
                  font-weight: 800;
                  bottom: -10px;
                  right: 15px;
                  color: rgba(255, 255, 255, 0.03);
                  z-index: 0;
                  transition: color 0.4s ease;
                  pointer-events: none;
                }

                .timeline-content.glass-panel:hover .timeline-step-bg {
                    color: rgba(160, 79, 244, 0.08);
                }

                /* Icon Box */
                .timeline-icon-box {
                  position: absolute;
                  width: 50px; /* Reduced from 60px */
                  height: 50px;
                  background: #0c001f;
                  border: 2px solid rgba(160, 79, 244, 0.6);
                  box-shadow: 0 0 10px rgba(160, 79, 244, 0.5);
                  border-radius: 50%;
                  top: 35px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  z-index: 5; /* Ensure it stays above everything */
                  font-size: 1.25rem;
                  color: #fff;
                  transition: all 0.3s ease;
                }

                .timeline-icon-box svg {
                    z-index: 6; /* Icons should be clearly visible */
                }

                .timeline-content.glass-panel:hover .timeline-icon-box {
                    background: #a04ff4;
                    border-color: #a04ff4;
                    box-shadow: 0 0 20px rgba(160, 79, 244, 0.8);
                    transform: scale(1.1);
                }

                /* Position slightly inside the line margins so they aren't cut off */
                .timeline-item:nth-child(odd) .timeline-icon-box { right: -25px; }
                .timeline-item:nth-child(even) .timeline-icon-box { left: -25px; }

                /* Content Text */
                .timeline-title {
                  font-size: 1.4rem;
                  font-weight: 700;
                  color: #fff;
                  margin-bottom: 12px;
                  position: relative;
                  z-index: 2;
                }

                .timeline-description {
                  font-size: 1rem;
                  line-height: 1.6;
                  color: #c0c0c0;
                  position: relative;
                  z-index: 2;
                  margin-bottom: 0;
                }

                /* =============================================
                   IMPROVED RESPONSIVE DESIGN
                   ============================================= */
                @media screen and (max-width: 991px) {
                  .process-section .section-heading { font-size: 2.6rem; }
                  .timeline-content.glass-panel { padding: 30px 25px; }
                  .timeline-item { padding: 10px 30px; }
                }

                @media screen and (max-width: 768px) {
                  .process-section {
                    padding: 80px 15px;
                    overflow: hidden; /* Restrict overflow on mobile to prevent crazy scrolling */
                  }
                  .process-section .section-heading {
                    font-size: 2.3rem;
                  }
                  
                  .timeline-container::after {
                    left: 20px;
                  }
                  
                  .timeline-item {
                    width: 100%;
                    padding-left: 60px;
                    padding-right: 0;
                  }
                  
                  .timeline-item:nth-child(odd),
                  .timeline-item:nth-child(even) {
                    left: 0;
                    margin-bottom: 30px;
                  }
                  
                  .timeline-icon-box {
                    width: 40px;
                    height: 40px;
                    font-size: 1rem;
                    top: 25px !important;
                  }
                  
                  .timeline-item:nth-child(odd) .timeline-icon-box,
                  .timeline-item:nth-child(even) .timeline-icon-box {
                    left: 0px; 
                  }
                  
                  .timeline-step-bg {
                    font-size: 4rem;
                    bottom: 0px;
                  }
                  
                  .timeline-title {
                      font-size: 1.25rem;
                  }
                }
            `}</style>
        </>
    );
};

export default ProcessSection;